import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { IApiResponse } from '../../../../shared/models/api-response';
import { environment } from '../../../../../environments/environment';
import { convertStringDateToJSDate } from '../../../../shared/utilities/date-utility';
import { IIecBallot } from '../models/iec/IIecBallot';
import { IIecBallotDetails } from '../models/iec/iec-ballot-details';
import { IUpdateIecBallotDatesRequest, IUpdateIecBallotNoteRequest } from '../models/iec/iec-update-request';
import { IIecBallotVote, IProxyVoteRequest, IVoteRequest } from '../models/iec/iec-ballot-vote';
import { IecBallotVoteHistoryDto } from '../models/iec/iec-ballot-vote-history';
import { IFinaliseIecBallotRequest } from '../models/iec/iec-finalise-vote';
import { IBlobFile } from '../../../../shared/models/blob-file';
import { convertObjectToFormData } from '../../../../shared/utilities/form-data-utility';

@Injectable({
  providedIn: 'root'
})
export class IecBallotService {

  baseUrl: string = `${environment.servicesUrl.ballot}api/ballots/iec`;

  constructor(
    private http: HttpClient,
  ) { }

  public createBallot(ballot: IIecBallot): Observable<IApiResponse<string>> {
    return this.http.post<IApiResponse<string>>(this.baseUrl, ballot);
  }

  public getBallot(id: string): Observable<IApiResponse<IIecBallotDetails>> {
    return this.http
      .get<IApiResponse<IIecBallotDetails>>(`${this.baseUrl}/${id}`)
      .pipe(
        // transfer to somewhere reusable if needed...
        map((rawResponse) => {
          let response = {...rawResponse};
          if(rawResponse.success && rawResponse.content){
            if(response.content.ballotSummary){
              response.content.ballotSummary.startDate = convertStringDateToJSDate(response.content.ballotSummary.startDate as any as string) as Date;
              response.content.ballotSummary.endDate = convertStringDateToJSDate(response.content.ballotSummary.endDate as any as string) as Date;
            }

            if(response.content.documents?.length > 0){
              response.content.documents.forEach(x => {
                x.createdOn = convertStringDateToJSDate(x.createdOn as any as string) as Date;
              });
            }

            if(response.content.iecBallotVotes?.length > 0){
              response.content.iecBallotVotes.forEach(x => {
                x.createDate = convertStringDateToJSDate(x.createDate as any as string);
                x.lastUpdated = convertStringDateToJSDate(x.lastUpdated as any as string)
              });
            }
          }

          return response;
        })
      );
  }
//UploadDrafts
  public uploadDraftFile(ballotId: string, file: File): Observable<IApiResponse<any>> {

    const formData = new FormData();
    formData.append("File", file);
    formData.append("DocumentType", "Draft");

    return this.http.post<IApiResponse<any>>(`${this.baseUrl}/${ballotId}/draft-documents`, formData)
  }

  public uploadCommentTemplateFile(ballotId: string, file: File): Observable<IApiResponse<any>> {
    const formData = new FormData();
    formData.append("File", file);
    formData.append("DocumentType", "Comment");

    return this.http.post<IApiResponse<any>>(`${this.baseUrl}/${ballotId}/comment-documents`, formData)
  }

  public getIECBallot(ballotId: string): Observable<IApiResponse<IIecBallotDetails>> {
    return this.http
      .get<IApiResponse<IIecBallotDetails>>(`${this.baseUrl}/${ballotId}`);
  }

  public updateBallotDates(request: IUpdateIecBallotDatesRequest): Observable<IApiResponse<any>> {
      return this.http
        .put<IApiResponse<any>>(`${this.baseUrl}/${request.ballotId}/dates`, request);
  }

  public updateBallotNotes(request: IUpdateIecBallotNoteRequest): Observable<IApiResponse<any>> {
    return this.http
      .put<IApiResponse<any>>(`${this.baseUrl}/${request.ballotId}/notes`, request);
  }

  public getDraftFiles(ballotId: string): Observable<IApiResponse<IBlobFile>> {
        return this.http.get<IApiResponse<IBlobFile>>(`${this.baseUrl}/${ballotId}/draft-documents`);
  }

  public getCommentTemplateFiles(ballotId: string): Observable<IApiResponse<IBlobFile>> {
        return this.http.get<IApiResponse<IBlobFile>>(`${this.baseUrl}/${ballotId}/comment-documents`);
  }

  public placeVote(vote: IVoteRequest): Observable<IApiResponse<any>> {
      return this.http.post<IApiResponse<any>>(`${this.baseUrl}/${vote.ballotId}/votes/${vote.voteId}`, vote);
  }

  public getIECBallotVoteHistory(IECBallotId: string, voteId: string) : Observable<IApiResponse<IecBallotVoteHistoryDto[]>> {
    return this.http.get<IApiResponse<IecBallotVoteHistoryDto[]>>(`${this.baseUrl}/${IECBallotId}/votes/${voteId}/histories`);
  }

  public submitProxyVote(request: IProxyVoteRequest) : Observable<HttpEvent<any>> {
      const formData = convertObjectToFormData(request);

      return this.http.post<HttpEvent<any>>(`${this.baseUrl}/${request.ballotId}/proxy-vote`, formData, {
        reportProgress: true,
        observe: 'events',
      });
  }

  public finaliseIECBallot(request: IFinaliseIecBallotRequest): Observable<IApiResponse<any>> {
    return this.http.post<IApiResponse<any>>(`${this.baseUrl}/${request.ballotId}/finalise`, request);
  }

}
